import { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { DataTableColumnHeader } from "./data-table-column-header";
import { DataTableRowActions } from "./data-table-row-actions";
import { CampaignTypeBadge } from "./campaign-type-badge";
import { cn } from "@/lib/utils";
import type { IPropagationCampaign, CampaignType } from "src/@types/campaign-propagation";

const STATUS_CONFIG = {
  PENDING: { variant: "outline" as const, label: "Pendente", className: "" },
  IN_PROGRESS: { variant: "secondary" as const, label: "Em Progresso", className: "" },
  SUCCESS: { variant: "default" as const, label: "Sucesso", className: "bg-green-500 hover:bg-green-600" },
  PARTIAL_FAILURE: { variant: "outline" as const, label: "Parcial", className: "bg-yellow-500 hover:bg-yellow-600 text-white" },
  FAILED: { variant: "destructive" as const, label: "Falha", className: "" },
};

export const columns: ColumnDef<IPropagationCampaign>[] = [
  {
    accessorKey: "campaign_type",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Tipo" />
    ),
    cell: ({ row }) => {
      const type = row.getValue("campaign_type") as CampaignType;
      return <CampaignTypeBadge type={type} />;
    },
  },
  {
    accessorKey: "campaign_code",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Código" />
    ),
    cell: ({ row }) => {
      const code = row.getValue("campaign_code") as number;
      return <div className="font-mono">#{code}</div>;
    },
  },
  {
    accessorKey: "total_stores",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Total Lojas" />
    ),
    cell: ({ row }) => {
      return <div className="text-center">{row.getValue("total_stores")}</div>;
    },
  },
  {
    accessorKey: "success_count",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sucesso" />
    ),
    cell: ({ row }) => {
      return (
        <div className="text-center">
          <span className="text-green-600 font-semibold">
            {row.getValue("success_count")}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "failed_count",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Falha" />
    ),
    cell: ({ row }) => {
      return (
        <div className="text-center">
          <span className="text-red-600 font-semibold">
            {row.getValue("failed_count")}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "pending_count",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Pendente" />
    ),
    cell: ({ row }) => {
      return (
        <div className="text-center">
          <span className="text-yellow-600 font-semibold">
            {row.getValue("pending_count")}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "overall_status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status Geral" />
    ),
    cell: ({ row }) => {
      const status = row.getValue("overall_status") as keyof typeof STATUS_CONFIG;
      const config = STATUS_CONFIG[status];
      return (
        <Badge variant={config.variant} className={cn(config.className)}>
          {config.label}
        </Badge>
      );
    },
  },
  {
    accessorKey: "last_propagated",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Última Propagação" />
    ),
    cell: ({ row }) => {
      const date = row.getValue("last_propagated") as string | null;
      return date ? new Date(date).toLocaleString("pt-BR") : "Nunca";
    },
  },
  {
    id: "actions",
    header: "Ações",
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
];
